/**
 * Online review by an approved inspection body (#/review/<token>, docs/decisions/0017): the
 * drawings read-only, and remarks pinned straight on them. The installer takes the pins over as
 * an inspection's remarks (Inspection → Reports), each remembering where it was pinned.
 */
import { useState } from 'react';
import type { Id, Op, Project } from '@elec/domain';
import { newId } from '@elec/domain';
import type { Translate } from '@elec/i18n';
import type { ReviewPin } from '@elec/sync';
import { getSymbol, elementBounds } from '@elec/symbols';
import { Dialog } from './ui.tsx';

/** Belgian approved bodies for electrical inspections (suggestions; any name can be typed). */
export const INSPECTION_BODIES = ['ACEG', 'BTV', 'Certinergie', 'Electro-Test', 'Kiwa', 'OCB', 'Vinçotte'];

const AUTHOR_PREF = 'tablo.reviewAuthor';
const remembered = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTHOR_PREF) ?? '{}') as { author?: string; body?: string };
  } catch {
    return {};
  }
};

/** The device or point code closest to a click (within 15 mm), to prefill "circuit or point". */
export function nearestTag(project: Project, sheetId: Id, p: { x: number; y: number }): string {
  let best = '';
  let dist = 15;
  for (const el of Object.values(project.elements)) {
    if (el.sheetId !== sheetId || !el.tag) continue;
    const def = getSymbol(el.symbolId);
    if (!def || def.room || def.freeText) continue;
    const b = elementBounds(def, el);
    const dx = Math.max(b.minX - p.x, 0, p.x - b.maxX);
    const dy = Math.max(b.minY - p.y, 0, p.y - b.maxY);
    const d = Math.hypot(dx, dy);
    if (d < dist) {
      dist = d;
      best = el.tag;
    }
  }
  return best;
}

export function PinDialog(props: {
  t: Translate;
  n: number;
  initialRef: string;
  onCancel(): void;
  onSave(pin: { author: string; body: string; text: string; article: string; ref: string }): Promise<void>;
}) {
  const { t } = props;
  const mem = remembered();
  const [author, setAuthor] = useState(mem.author ?? '');
  const [body, setBody] = useState(mem.body ?? '');
  const [text, setText] = useState('');
  const [article, setArticle] = useState('');
  const [ref, setRef] = useState(props.initialRef);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = author.trim().length >= 2 && text.trim().length >= 2 && !busy;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      try {
        localStorage.setItem(AUTHOR_PREF, JSON.stringify({ author: author.trim(), body: body.trim() }));
      } catch {
        /* private mode: asked again next time */
      }
      await props.onSave({
        author: author.trim(),
        body: body.trim(),
        text: text.trim(),
        article: article.trim(),
        ref: ref.trim(),
      });
    } catch (e) {
      setError(t('review.failed', { msg: (e as Error).message }));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('review.title', { n: props.n })}
      onClose={props.onCancel}
      actions={
        <>
          <button onClick={props.onCancel}>{t('dialog.cancel')}</button>
          <button className="primary" disabled={!ok} onClick={() => void save()} data-testid="pin-save">
            {t('review.save')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label htmlFor="pin-author">{t('review.author')}</label>
          <input
            id="pin-author"
            value={author}
            autoComplete="name"
            onChange={(e) => setAuthor(e.target.value)}
            data-testid="pin-author"
          />
        </div>
        <div className="field">
          <label htmlFor="pin-body">{t('review.body')}</label>
          <input
            id="pin-body"
            list="pin-bodies"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="pin-body"
          />
          <datalist id="pin-bodies">
            {INSPECTION_BODIES.map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
        </div>
        <div className="field wide">
          <label htmlFor="pin-text">{t('review.text')}</label>
          <textarea
            id="pin-text"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="pin-text"
          />
        </div>
        <div className="field">
          <label htmlFor="pin-article">{t('review.article')}</label>
          <input
            id="pin-article"
            value={article}
            placeholder="art. 86"
            onChange={(e) => setArticle(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pin-ref">{t('review.ref')}</label>
          <input id="pin-ref" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="pin-ref" />
        </div>
      </div>
      {error && <p className="warn">{error}</p>}
    </Dialog>
  );
}

export function PinsDialog(props: {
  t: Translate;
  pins: ReviewPin[];
  sheetName(id: Id): string;
  onShow(pin: ReviewPin): void;
  onDelete(pin: ReviewPin): void;
  onClose(): void;
}) {
  const { t } = props;
  return (
    <Dialog
      title={t('review.list')}
      wide
      onClose={props.onClose}
      actions={<button onClick={props.onClose}>{t('dialog.close')}</button>}
    >
      {props.pins.length === 0 && <p className="muted">{t('review.none')}</p>}
      <ol className="pin-list" data-testid="pin-list">
        {props.pins.map((p) => (
          <li key={p.id}>
            <div>
              <strong>{[p.article, p.ref].filter(Boolean).join(' · ') || t('review.text')}</strong> {p.text}
              <br />
              <span className="muted small">
                {t('review.sheet', { name: props.sheetName(p.sheetId) })} · {p.author}
                {p.body && ` (${p.body})`}
                {p.status === 'imported' && ` · ${t('review.taken')}`}
              </span>
            </div>
            <div className="pin-actions">
              <button className="link" onClick={() => props.onShow(p)}>
                {t('inspection.showPin')}
              </button>
              {p.status === 'new' && (
                <button className="link" onClick={() => props.onDelete(p)}>
                  {t('review.delete')}
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Dialog>
  );
}

/** The pins as an inspection with its remarks (one undo step), each remembering its pin. */
export function pinsToOps(
  pins: ReviewPin[],
  reference: string,
  today: string,
): { ops: Op[]; inspectionId: Id } {
  const inspectionId = newId();
  const count = new Map<string, number>();
  for (const p of pins) if (p.body) count.set(p.body, (count.get(p.body) ?? 0) + 1);
  const body = [...count].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  const authors = [...new Set(pins.map((p) => p.author))].join(', ');
  const ops: Op[] = [
    {
      op: 'add',
      coll: 'inspections',
      value: {
        id: inspectionId,
        date: [...pins].map((p) => p.created.slice(0, 10)).sort()[0] ?? today,
        body,
        reference,
        inspector: authors,
        result: 'unknown',
        pages: [],
        notes: '',
      },
    },
    ...pins.map((p, order): Op => ({
      op: 'add',
      coll: 'remarks',
      value: {
        id: newId(),
        inspectionId,
        order,
        text: p.text,
        article: p.article,
        ref: p.ref.toUpperCase(),
        status: 'open',
        fixNote: '',
        photos: [],
        pin: { sheetId: p.sheetId, x: p.x, y: p.y },
        author: p.author,
      },
    })),
  ];
  return { ops, inspectionId };
}
